import { useEffect, useState } from "react";
import { CheckCircle, Warning, Signature as SignatureIcon, FileText, FilePdf } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../../lib/api";

export default function ClientForms() {
  const [data, setData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [details, setDetails] = useState("");
  const [parqSig, setParqSig] = useState("");
  const [extra, setExtra] = useState({
    emergency_contact_name: "", emergency_contact_phone: "", activity_level: "",
    goals: "", injuries: "", limitations: "",
    favourite_movie: "", sports_played: "", favourite_sport_watch: "", hobbies: "",
  });
  const [waiverSig, setWaiverSig] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data: d } = await api.get("/forms/onboarding");
      setData(d);
    } catch (_e) {}
  };
  useEffect(() => { load(); }, []);

  if (!data) return <div className="tracker text-zinc-500">Loading…</div>;

  const anyYes = Object.values(answers).some(Boolean);
  const allAnswered = data.parq_questions.every((q) => answers[q.id] !== undefined);
  const setX = (k) => (e) => setExtra((s) => ({ ...s, [k]: e.target.value }));

  const submitParq = async () => {
    setBusy(true);
    try {
      await api.post("/forms/parq", {
        answers,
        details: details || null,
        signature_name: parqSig,
        ...Object.fromEntries(Object.entries(extra).map(([k, v]) => [k, v || null])),
      });
      toast.success("PAR-Q submitted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submit failed");
    } finally { setBusy(false); }
  };

  const signWaiver = async () => {
    setBusy(true);
    try {
      await api.post("/forms/waiver", { signature_name: waiverSig, agreed });
      toast.success("Waiver signed");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Signing failed");
    } finally { setBusy(false); }
  };


  return (
    <div className="space-y-8" data-testid="client-forms-page">
      <div>
        <div className="tracker text-brand mb-3">[ Health & legal ]</div>
        <h1 className="font-display text-4xl sm:text-5xl">FORMS.</h1>
        <p className="text-zinc-400 mt-3">Two quick forms before training: a health questionnaire and a liability waiver.</p>
      </div>

      {/* PAR-Q */}
      <section className="card-flat p-6 sm:p-8" data-testid="parq-section">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="tracker text-zinc-500">[ PAR-Q+ · NEW CLIENT FITNESS SCREENING ]</div>
          <div className="flex items-center gap-3">
            {data.parq_pdf_url && (
              <a href={data.parq_pdf_url} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white"
                data-testid="parq-pdf-link">
                <FilePdf size={16} weight="duotone" className="text-[#FF6B61]" /> Original PDF
              </a>
            )}
            {data.parq && <CheckCircle size={20} weight="fill" className="text-emerald-400" data-testid="parq-done-icon" />}
          </div>
        </div>
        {data.parq ? (
          <div data-testid="parq-done">
            <div className="text-sm text-zinc-300">
              Submitted on {new Date(data.parq.created_at).toLocaleDateString()} — signed <span className="font-mono">{data.parq.signature_name}</span>.
            </div>
            {data.parq.needs_review && (
              <div className="mt-3 p-3 border border-amber-500/40 bg-amber-500/10 text-sm text-amber-300 flex gap-2" data-testid="parq-flagged-note">
                <Warning size={16} weight="duotone" className="shrink-0 mt-0.5" />
                You flagged a health concern — your coach has been notified and may follow up before your next session.
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {data.parq_intro && (
              <p className="text-sm text-zinc-400" data-testid="parq-intro">{data.parq_intro}</p>
            )}

            {/* Emergency contact */}
            <div>
              <div className="tracker text-[10px] text-zinc-500 mb-2">EMERGENCY CONTACT</div>
              <div className="grid sm:grid-cols-2 gap-3">
                <input value={extra.emergency_contact_name} onChange={setX("emergency_contact_name")} placeholder="Emergency contact name" className="px-3 py-2 border bg-[#0A0A0A] text-sm" data-testid="parq-emergency-name" />
                <input value={extra.emergency_contact_phone} onChange={setX("emergency_contact_phone")} placeholder="Emergency contact phone" className="px-3 py-2 border bg-[#0A0A0A] text-sm font-mono" data-testid="parq-emergency-phone" />
              </div>
            </div>

            {/* Health questions */}
            <div>
              <div className="tracker text-[10px] text-zinc-500 mb-1">GENERAL HEALTH QUESTIONS</div>
              {data.parq_questions.map((q, i) => (
                <div key={q.id} className="flex flex-col sm:flex-row sm:items-center gap-2 py-3 border-b border-white/5" data-testid={`parq-${q.id}`}>
                  <div className="flex-1 text-sm text-zinc-300"><span className="text-zinc-600 font-mono mr-2">{i + 1}.</span>{q.text}</div>
                  <div className="flex gap-2 shrink-0">
                    {[true, false].map((v) => (
                      <button
                        key={String(v)}
                        onClick={() => setAnswers((a) => ({ ...a, [q.id]: v }))}
                        className={`px-4 py-1.5 border text-xs tracker ${
                          answers[q.id] === v
                            ? v ? "bg-[#FF6B61]/20 border-[#FF6B61]/60 text-[#FF6B61]" : "bg-emerald-500/20 border-emerald-500/50 text-emerald-300"
                            : "border-white/10 text-zinc-500 hover:border-white/30"
                        }`}
                        data-testid={`parq-${q.id}-${v ? "yes" : "no"}`}
                      >
                        {v ? "YES" : "NO"}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              {data.parq_guidance && (
                <p className="text-xs text-zinc-500 mt-3" data-testid="parq-guidance">{data.parq_guidance}</p>
              )}
            </div>

            {anyYes && (
              <div>
                <div className="text-sm text-zinc-300 mb-2">Please give details on any YES answers</div>
                <textarea value={details} onChange={(e) => setDetails(e.target.value)} rows={3} maxLength={3000} className="w-full px-3 py-2 border bg-[#0A0A0A] text-sm" data-testid="parq-details" />
              </div>
            )}

            {/* Activity background & goals */}
            <div className="space-y-3">
              <div className="tracker text-[10px] text-zinc-500">ACTIVITY BACKGROUND & GOALS</div>
              <div>
                <div className="text-sm text-zinc-300 mb-2">Current activity level</div>
                <div className="flex flex-wrap gap-2">
                  {(data.activity_levels || []).map((lvl) => (
                    <button key={lvl} type="button" onClick={() => setExtra((s) => ({ ...s, activity_level: lvl }))}
                      className={`px-4 py-1.5 border text-xs tracker ${extra.activity_level === lvl ? "bg-brand/20 border-brand text-brand-lighter" : "border-white/10 text-zinc-500 hover:border-white/30"}`}
                      data-testid={`parq-activity-${lvl.toLowerCase().replace(/ /g, "-")}`}>
                      {lvl}
                    </button>
                  ))}
                </div>
              </div>
              <input value={extra.goals} onChange={setX("goals")} placeholder="Primary goal(s)" className="w-full px-3 py-2 border bg-[#0A0A0A] text-sm" data-testid="parq-goals" />
              <input value={extra.injuries} onChange={setX("injuries")} placeholder="Past injuries or surgeries" className="w-full px-3 py-2 border bg-[#0A0A0A] text-sm" data-testid="parq-injuries" />
              <input value={extra.limitations} onChange={setX("limitations")} placeholder="Any current pain / limitations" className="w-full px-3 py-2 border bg-[#0A0A0A] text-sm" data-testid="parq-limitations" />
            </div>

            {/* Getting to know you */}
            <div className="space-y-3">
              <div className="tracker text-[10px] text-zinc-500">GETTING TO KNOW YOU</div>
              <div className="grid sm:grid-cols-2 gap-3">
                <input value={extra.favourite_movie} onChange={setX("favourite_movie")} placeholder="Favourite movie" className="px-3 py-2 border bg-[#0A0A0A] text-sm" data-testid="parq-movie" />
                <input value={extra.sports_played} onChange={setX("sports_played")} placeholder="Sport(s) you play" className="px-3 py-2 border bg-[#0A0A0A] text-sm" data-testid="parq-sports" />
                <input value={extra.favourite_sport_watch} onChange={setX("favourite_sport_watch")} placeholder="Favourite sport (to watch)" className="px-3 py-2 border bg-[#0A0A0A] text-sm" data-testid="parq-sport-watch" />
                <input value={extra.hobbies} onChange={setX("hobbies")} placeholder="Hobbies / interests" className="px-3 py-2 border bg-[#0A0A0A] text-sm" data-testid="parq-hobbies" />
              </div>
            </div>

            {/* Declaration + signature */}
            {data.parq_declaration && (
              <div className="p-4 bg-[#0A0A0A] border border-white/10 text-xs text-zinc-500" data-testid="parq-declaration">
                <div className="tracker text-[9px] text-zinc-600 mb-2">CLIENT DECLARATION</div>
                {data.parq_declaration}
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-3 sm:items-end pt-2">
              <div className="flex-1">
                <div className="tracker text-[10px] text-zinc-500 mb-1">TYPE YOUR FULL LEGAL NAME TO SIGN</div>
                <input value={parqSig} onChange={(e) => setParqSig(e.target.value)} placeholder="Full name" className="w-full px-3 py-2 border bg-[#0A0A0A] font-mono" data-testid="parq-signature" />
              </div>
              <button onClick={submitParq} disabled={busy || !allAnswered || parqSig.trim().length < 2} className="btn-primary disabled:opacity-40" data-testid="parq-submit">
                <SignatureIcon size={16} weight="bold" /> Sign & submit
              </button>
            </div>
          </div>
        )}
      </section>

      {/* Waiver */}
      <section className="card-flat p-6 sm:p-8" data-testid="waiver-section">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="tracker text-zinc-500">[ LIABILITY WAIVER ]</div>
          {data.waiver && <CheckCircle size={20} weight="fill" className="text-emerald-400" data-testid="waiver-done-icon" />}
        </div>
        {data.waiver ? (
          <div className="text-sm text-zinc-300" data-testid="waiver-done">
            Signed on {new Date(data.waiver.created_at).toLocaleDateString()} — <span className="font-mono">{data.waiver.signature_name}</span>.
          </div>
        ) : (
          <div className="space-y-4">
            <div className="max-h-56 overflow-y-auto p-4 bg-[#0A0A0A] border border-white/10 text-sm text-zinc-400 whitespace-pre-wrap flex gap-3" data-testid="waiver-text">
              <FileText size={18} weight="duotone" className="text-zinc-600 shrink-0" />
              <div>{data.waiver_text}</div>
            </div>
            <label className="flex items-start gap-3 cursor-pointer text-sm text-zinc-300">
              <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-0.5" data-testid="waiver-agree" />
              I have read and agree to the terms above.
            </label>
            <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
              <div className="flex-1">
                <div className="tracker text-[10px] text-zinc-500 mb-1">TYPE YOUR FULL LEGAL NAME TO SIGN</div>
                <input value={waiverSig} onChange={(e) => setWaiverSig(e.target.value)} placeholder="Full name" className="w-full px-3 py-2 border bg-[#0A0A0A] font-mono" data-testid="waiver-signature" />
              </div>
              <button onClick={signWaiver} disabled={busy || !agreed || waiverSig.trim().length < 2} className="btn-primary disabled:opacity-40" data-testid="waiver-submit">
                <SignatureIcon size={16} weight="bold" /> Sign waiver
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
